import { useState, useEffect, useCallback, useRef } from 'react';

export const resolveChatApi = () => {
  if (import.meta.env.VITE_API_BASE_URL) {
    return `${import.meta.env.VITE_API_BASE_URL}/chat.php`;
  }
  if (typeof window !== 'undefined') {
    if (window.location.port === '5173') {
      return '/api/chat.php';
    }
    const path = window.location.pathname;
    const match = path.match(/^\/([^\/]+)/);
    if (match && match[1] && !['api', 'dist', 'src'].includes(match[1])) {
      return `/${match[1]}/api/chat.php`;
    }
    return 'http://localhost/x2infotech_full_integrated_project/api/chat.php';
  }
  return '/api/chat.php';
};

const TOKEN_KEY = 'x2infotech_token';

export const useChat = () => {
  const [channels, setChannels] = useState([]);
  const [activeChannel, setActiveChannel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [replyTo, setReplyTo] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    const userJson = localStorage.getItem('x2infotech_user');
    if (userJson) {
      try {
        setCurrentUser(JSON.parse(userJson));
      } catch (e) {
        console.error('Failed to parse user from localStorage');
      }
    }
  }, []);

  const pollInterval = useRef(null);

  const fetchApi = async (action, options = {}) => {
    setLoading(true);
    setError(null);
    const token = localStorage.getItem(TOKEN_KEY);
    const apiBase = resolveChatApi();
    const url = `${apiBase}?action=${action}`;

    const defaultHeaders = {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };

    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          ...defaultHeaders,
          ...options.headers
        }
      });

      const text = await response.text();
      console.log(`API Response (${action}):`, text.substring(0, 100));

      if (response.status === 401) {
        console.error("401 Unauthorized API Response:", text);
        setLoading(false);
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem('x2infotech_user');
        setCurrentUser(null);
        setError('Your session has expired. Please log in again.');
        window.dispatchEvent(new Event('force_login'));
        return { success: false, message: 'Unauthorized' };
      }

      try {
        const data = JSON.parse(text);
        setLoading(false);
        return data;
      } catch (e) {
        console.error(`JSON Parse Error (${action}):`, text);
        setLoading(false);
        setError('Server returned invalid data. Check PHP errors.');
        return { success: false, message: 'Invalid JSON' };
      }
    } catch (err) {
      console.error(`Fetch API Error (${action}):`, err);
      setLoading(false);
      setError('Connection failed. Is XAMPP running?');
      throw err;
    }
  };

  const loadChannels = useCallback(async () => {
    try {
      const data = await fetchApi('get_channels');
      if (data.success) {
        setChannels(data.channels);
      } else if (data.message !== 'Unauthorized') {
        setError(data.message);
      }
    } catch (err) {
      // Error handled in fetchApi
    }
  }, []);

  const loadMessages = useCallback(async (channelId) => {
    if (!channelId) return;
    try {
      const data = await fetchApi(`get_messages&channel_id=${channelId}`);
      if (data.success) {
        setMessages(data.messages);
      }
    } catch (err) {
      // Error handled in fetchApi
    }
  }, []);

  const loadMembers = useCallback(async (channelId) => {
    // Currently get_members might not be implemented in chat.php 
    // but the get_channels already returns some member info.
    // For now, we can just use dummy or skip if not critical.
  }, []);

  const sendMessage = useCallback(async (content, parentId = null) => {
    if (!activeChannel) return;

    try {
      const data = await fetchApi('send_message', {
        method: 'POST',
        body: JSON.stringify({
          channel_id: activeChannel.id,
          content,
          parent_id: parentId
        })
      });

      if (data.success) {
        setMessages(prev => [...prev, data.message]);
        return data.message;
      } else {
        if (data.message === 'Unauthorized') {
          console.warn('Unauthorized attempt to send message');
        } else {
          throw new Error(data.message);
        }
      }
    } catch (err) {
      if (err.message !== 'Unauthorized') {
        console.error('Failed to send message:', err);
      }
    }
  }, [activeChannel]);

  const createChannel = useCallback(async (channelData) => {
    try {
      const data = await fetchApi('create_channel', {
        method: 'POST',
        body: JSON.stringify(channelData)
      });

      if (data.success) {
        await loadChannels();
        return data.channel_id;
      } else {
        throw new Error(data.message);
      }
    } catch (err) {
      alert('Failed to create channel: ' + err.message);
    }
  }, [loadChannels]);

  const joinChannel = useCallback(async (channelId) => {
    try {
      const data = await fetchApi(`join_channel&id=${channelId}`);
      if (data.success) {
        await loadChannels();
      }
    } catch (err) {
      console.error('Failed to join channel');
    }
  }, [loadChannels]);

  const leaveChannel = useCallback(async (channelId) => {
    try {
      const data = await fetchApi(`leave_channel&id=${channelId}`);
      if (data.success) {
        await loadChannels();
        if (activeChannel?.id === channelId) {
          setActiveChannel(null);
          setMessages([]);
        }
      }
    } catch (err) {
      console.error('Failed to leave channel');
    }
  }, [activeChannel, loadChannels]);

  const fetchUsers = useCallback(async () => {
    try {
      const data = await fetchApi('get_all_users');
      if (data.success) {
        return data.users;
      }
      return [];
    } catch (err) {
      console.error('Failed to fetch users:', err);
      return [];
    }
  }, []);

  const addMember = useCallback(async (channelId, userId) => {
    try {
      const data = await fetchApi('add_member', {
        method: 'POST',
        body: JSON.stringify({
          channel_id: channelId,
          user_id: userId
        })
      });

      if (data.success) {
        await loadChannels();
        return true;
      } else {
        throw new Error(data.message);
      }
    } catch (err) {
      console.error('Failed to add member:', err);
      throw err;
    }
  }, [loadChannels]);

  const deleteMessage = useCallback(async (messageId) => {
    try {
      const data = await fetchApi(`delete_message&id=${messageId}`, {
        method: 'POST',
        body: JSON.stringify({ message_id: messageId })
      });
      if (data.success) {
        setMessages(prev => prev.filter(m => m.id !== messageId));
        return true;
      } else {
        throw new Error(data.message || 'Failed to delete message');
      }
    } catch (err) {
      console.error('Delete message error:', err);
      alert(err.message || 'Failed to delete message');
      return false;
    }
  }, []);

  const deleteChannel = useCallback(async (channelId) => {
    try {
      const data = await fetchApi(`delete_channel&id=${channelId}`, {
        method: 'POST',
        body: JSON.stringify({ channel_id: channelId })
      });
      if (data.success) {
        await loadChannels();
        if (activeChannel?.id === channelId) {
          setActiveChannel(null);
          setMessages([]);
        }
        return true;
      } else {
        throw new Error(data.message || 'Failed to delete channel');
      }
    } catch (err) {
      console.error('Delete channel error:', err);
      alert(err.message || 'Failed to delete channel');
      return false;
    }
  }, [activeChannel, loadChannels]);

  const createOrGetDirectChannel = useCallback(async (targetUserId) => {
    try {
      const data = await fetchApi('create_or_get_direct_channel', {
        method: 'POST',
        body: JSON.stringify({ target_user_id: targetUserId })
      });
      if (data.success && data.channel_id) {
        await loadChannels();
        const chan = { id: data.channel_id, name: data.channel?.name || 'Direct Chat', type: 'private' };
        selectChannel(chan);
        return chan;
      } else {
        throw new Error(data.message || 'Failed to open direct conversation');
      }
    } catch (err) {
      console.error('Direct chat error:', err);
      alert(err.message || 'Could not start direct conversation');
    }
  }, [loadChannels, selectChannel]);

  const uploadAttachment = useCallback(async (file, messageText = '') => {
    if (!activeChannel) return;

    const token = localStorage.getItem(TOKEN_KEY);
    const userJson = localStorage.getItem('x2infotech_user');
    let userId = null;
    try {
      userId = JSON.parse(userJson)?.id;
    } catch (e) {}

    if (!userId) {
      alert('Please log in to upload files.');
      return;
    }

    const formData = new FormData();
    formData.append('channel_id', activeChannel.id);
    formData.append('user_id', userId);
    formData.append('content', messageText || file.name);
    formData.append('file', file);

    try {
      const response = await fetch(
        (import.meta.env.VITE_API_BASE_URL || '/api') + '/sendMessage.php',
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        }
      );

      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch (e) {
        throw new Error('Server returned invalid response');
      }

      if (data.status) {
        // Reload messages to show the new message with attachment
        await loadMessages(activeChannel.id);
        return data;
      } else {
        throw new Error(data.error || 'Upload failed');
      }
    } catch (err) {
      console.error('File upload error:', err);
      throw err;
    }
  }, [activeChannel, loadMessages]);

  const selectChannel = useCallback((channel) => {
    setActiveChannel(channel);
    setReplyTo(null);
    if (channel) {
      loadMessages(channel.id);
    }
  }, [loadMessages]);

  // Initial load
  useEffect(() => {
    loadChannels();
  }, [loadChannels]);

  // Real-time Polling
  useEffect(() => {
    if (activeChannel) {
      if (pollInterval.current) clearInterval(pollInterval.current);

      pollInterval.current = setInterval(() => {
        loadMessages(activeChannel.id);
      }, 3000); // Poll every 3 seconds
    }

    return () => {
      if (pollInterval.current) clearInterval(pollInterval.current);
    };
  }, [activeChannel, loadMessages]);

  return {
    channels,
    activeChannel,
    messages,
    members,
    loading,
    error,
    replyTo,
    setReplyTo,
    currentUser,
    selectChannel,
    sendMessage,
    createChannel,
    joinChannel,
    leaveChannel,
    fetchUsers,
    addMember,
    deleteMessage,
    deleteChannel,
    createOrGetDirectChannel,
    uploadAttachment,
    refreshChannels: loadChannels
  };
};
